<template>
    <modal
        :id="modalId"
        dialog-class="add-widget-modal"
        title="Add Widget"
        ok-only
        size="lg"
        scrollable
        ok-title="Close"
        ok-variant="default"
        @show="fetchData"
        @hide="clearData"
    >
        <busy-modal :show="busy" :overlay="true" />
        <b-row class="flex-column flex-md-row">
            <b-col>
                <form-input floatingLabel="Search" v-model="search" @input="searchWidgets" />
                <h6 class="text-secondary">Categories</h6>
                <b-list-group class="category-list">
                    <b-list-group-item
                        :key="category.id"
                        v-for="category in categories"
                        class="category-item d-flex justify-content-between align-items-center"
                        :class="{ 'active-category': category.active }"
                        @click="setCategory(category)"
                    >
                        {{ category.name }}
                        <b-badge variant="secondary" pill>{{ category.widgets_count }}</b-badge>
                    </b-list-group-item>
                </b-list-group>
            </b-col>
            <b-col class="widgets-list-wrapper">
                <h6 class="d-flex d-md-none text-secondary">Widgets</h6>
                <p v-if="!widgetsList.length">No avalaible widgets</p>
                <b-list-group class="widgets-list">
                    <b-list-group-item :key="widget.id" v-for="widget in widgetsList" class="mb-2 d-flex widget-item">
                        <div
                            class="widget-image mr-3 d-none d-sm-flex"
                            :style="{
                                'background-image': `url(${widget.preview_image_url})`,
                            }"
                        ></div>
                        <div class="d-flex w-100 justify-content-between align-items-baseline">
                            <div class="mr-3">
                                <span class="mb-1 widget-title">{{ getWidgetTitle(widget) }}</span>
                                <p class="d-none d-sm-block widget-desc text-secondary">
                                    {{ widget.description }}
                                </p>
                            </div>
                            <responsive-button variant="default" :icon="['fas', 'plus']" @click="addWidget(widget)"
                                >Add Widget</responsive-button
                            >
                        </div>
                    </b-list-group-item>
                </b-list-group>
            </b-col>
        </b-row>
    </modal>
</template>

<script>
    import API from '../../api';
    import { DASHBOARD_WIDGET_LIST_ENDPOINT, DASHBOARD_WIDGET_CATEGORIES_LIST_ENDPOINT } from '../../endpoints';
    import debounce from 'lodash/debounce';
    import { formatResponseError } from '../../utils/api';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import modal from '../generic/modal/modal.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import { mapGetters } from 'vuex';

    export default {
        components: { modal, FormInput, ResponsiveButton, BusyModal },
        name: 'AddWidgetModal',
        props: {
            modalId: String,
            dashboardTabId: Number,
        },
        data() {
            return {
                busy: null,
                categories: [],
                widgets: [],
                selectedCategory: null,
                search: null,
            };
        },
        computed: {
            ...mapGetters('header', ['userIsAdmin']),
            widgetsList() {
                return this.widgets;
            },
        },
        methods: {
            clearData() {
                this.selectedCategory = null;
                this.search = null;
            },
            fetchData() {
                Promise.all[(this.fetchCategories(), this.fetchWidgets())];
            },
            async fetchWidgets() {
                try {
                    this.busy = true;
                    const response = await API.get(DASHBOARD_WIDGET_LIST_ENDPOINT(), {
                        params: {
                            category_id: this.selectedCategory,
                            tab_id: this.dashboardTabId,
                            search: this.search,
                            for_admin: this.userIsAdmin,
                        },
                    });
                    this.widgets = response.data;
                } catch (e) {
                    this.errorToast({ text: 'Could not load widgets' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchCategories() {
                try {
                    this.busy = true;
                    const response = await API.get(DASHBOARD_WIDGET_CATEGORIES_LIST_ENDPOINT(), {
                        params: {
                            tab_id: this.dashboardTabId,
                            widget_search: this.search,
                            for_admin: this.userIsAdmin,
                        },
                    });
                    const allWidgetsCount = response.data.reduce((accumulator, curr) => {
                        return accumulator + curr.widgets_count;
                    }, 0);

                    this.categories = [
                        {
                            id: 'ALL',
                            name: 'All',
                            widgets_count: allWidgetsCount,
                            active: true,
                            is_all: true,
                        },
                        ...response.data,
                    ];
                } catch (e) {
                    this.errorToast({ text: 'Could not load categories' });
                } finally {
                    this.busy = false;
                }
            },
            async addWidget(widget) {
                this.busy = true;
                try {
                    await this.$store.dispatch('dashboard/createDashboardTabWidget', widget);
                    this.$emit('success-create');
                    this.$nextTick(() => {
                        this.$bvModal.hide(this.modalId);
                    });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not add widget to the dashboard.',
                    });
                } finally {
                    this.busy = false;
                }
            },
            getWidgetTitle(widget) {
                return widget.name;
            },
            searchWidgets: debounce(async function (value) {
                Promise.all([this.fetchWidgets(), this.fetchCategories()]);
            }, 500),
            setCategory(category) {
                this.categories = this.categories.map((item) => ({
                    ...item,
                    active: category.id === item.id,
                }));
                this.selectedCategory = category.is_all ? null : category.id;
                this.fetchWidgets();
            },
        },
    };
</script>

<style lang="scss" scoped>
    .add-widget-modal {
        .category-item {
            border: none;
            color: #0d47a1;
            cursor: pointer;
            &:hover {
                color: #212529;
                background-color: #e9ecef;
            }
        }
        .active-category {
            color: #212529;
            background-color: #e9ecef;
        }
        .widgets-list-wrapper {
            flex: 3;
            .widget-item {
                border: none;
                border-bottom: 1px solid #cdcdcd;
            }
            .widget-title {
                font-size: 18px;
            }
            .widget-desc {
                font-size: 14px;
            }
            .widget-image {
                width: 150px;
                height: 80px;
                border: 1px solid #cdcdcd;
                border-radius: 3px;
                background-size: auto 100%;
            }
        }
    }
</style>
